import React, { useEffect, useState } from 'react';
import { buildApiUrl } from '../config/api';

const ENROLLMENT_API_URL = buildApiUrl('/users/enrollments/');

const ContactForm = ({ onVisibilityChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    class: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isMinimized, setIsMinimized] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let prevIsMobile = null;

    const syncWithViewport = () => {
      const mobile = window.innerWidth < 768;

      if (prevIsMobile !== mobile) {
        setIsMinimized(mobile);
        prevIsMobile = mobile;
      }
    };

    syncWithViewport();
    window.addEventListener('resize', syncWithViewport);
    return () => window.removeEventListener('resize', syncWithViewport);
  }, []);

  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(!isMinimized);
    }
  }, [isMinimized, onVisibilityChange]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleNavMenuOpen = (event) => {
      if (event?.detail?.open) {
        setIsMinimized(true);
      }
    };

    window.addEventListener('nav-menu-open', handleNavMenuOpen);
    return () => window.removeEventListener('nav-menu-open', handleNavMenuOpen);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!isMinimized) {
      window.dispatchEvent(new CustomEvent('enrol-form-open'));
    }
  }, [isMinimized]);

  const extractErrorMessage = async (response) => {
    try {
      const contentType = response.headers.get('Content-Type') || '';

      if (contentType.includes('application/json')) {
        const data = await response.json();

        const normalise = (value) => {
          if (!value) {
            return [];
          }

          if (Array.isArray(value)) {
            return value.flatMap(normalise);
          }

          if (typeof value === 'object') {
            return Object.values(value).flatMap(normalise);
          }

          return [String(value)];
        };

        const messages = normalise(data);
        if (messages.length > 0) {
          return messages.join(' ');
        }
      }

      const text = await response.text();
      if (text) {
        return text;
      }
    } catch (error) {
      console.error('Failed to parse enrollment error response', error);
    }

    return `Request failed with status ${response.status}`;
  };

  const handleExpand = () => setIsMinimized(false);
  const handleCollapse = () => setIsMinimized(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setShowSuccess(false);

    const payload = {
      name: formData.name.trim(),
      mobile: formData.mobile.trim(),
      class_level: formData.class,
    };

    try {
      const response = await fetch(ENROLLMENT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const message = await extractErrorMessage(response);
        throw new Error(message || 'Failed to submit enrollment');
      }

  setShowSuccess(true);
  setFormData({ name: '', mobile: '', class: '' });

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Enrollment submission failed', error);
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className={`${isMinimized ? 'w-auto' : 'w-[260px]'} transition-all duration-300`}>
      {isMinimized ? (
        <button
          type="button"
          aria-label="Open contact form"
          onClick={handleExpand}
          className="group flex items-center gap-2 rounded-full bg-brand-yellow px-4 py-2 md:px-5 md:py-3 text-sm md:text-base text-text-primary shadow-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 min-w-[104px] md:min-w-[140px]"
        >
          <span className="text-sm font-semibold tracking-wide">Enrol Now</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5"
          >
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
      ) : (
        <div className="w-full rounded-2xl border border-primary/50 bg-surface p-5 shadow-glow">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg md:text-xl font-semibold text-primary tracking-tight">Enrol now</h3>
            <button
              type="button"
              aria-label="Minimize contact form"
              onClick={handleCollapse}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors duration-200 hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5"
              >
                <path d="M6 12h12" />
              </svg>
              <span className="sr-only">Minimize</span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="transform transition-all duration-300 hover:translate-y-[-4px]">
            {showSuccess && (
              <div className="mb-3 rounded-md bg-success/10 p-2 text-xs font-medium status-success animate-fade-in">
                Thank you.  We will contact you shortly.
              </div>
            )}
            {errorMessage && (
              <div className="mb-3 rounded-md bg-error/10 p-2 text-xs font-medium text-error animate-fade-in">
                {errorMessage}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label htmlFor="name" className="mb-1 block text-xs text-text-primary">
                  Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field py-2 text-sm"
                  minLength={2}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="mobile" className="mb-1 block text-xs text-text-primary">
                  Mobile <span className="text-error">*</span>
                </label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="input-field py-2 text-sm"
                  pattern="[0-9]{10}"
                  title="Please enter a valid 10-digit mobile number"
                  inputMode="numeric"
                  maxLength={10}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="class" className="mb-1 block text-xs text-text-primary">
                  Class <span className="text-error">*</span>
                </label>
                <select
                  id="class"
                  name="class"
                  value={formData.class}
                  onChange={handleChange}
                  className="input-field py-2 text-sm"
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Class</option>
                  <option value="Class 8">Class 8</option>
                  <option value="Class 9">Class 9</option>
                  <option value="Class 10">Class 10</option>
                  <option value="Class 11">Class 11</option>
                  <option value="Class 12">Class 12</option>
                  <option value="Dropper">Dropper</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full btn-primary py-2.5 text-sm font-semibold ${isSubmitting ? 'cursor-not-allowed opacity-75' : ''}`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <svg className="-ml-1 mr-3 h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </div>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ContactForm;